import { DOCUMENT } from '@angular/common';
import { Injectable, OnDestroy, inject } from '@angular/core';
import { computePosition, flip, offset, shift } from '@floating-ui/dom';

import {
  TOOLTIP_CLASS,
  TOOLTIP_ELEMENT_ID,
  TOOLTIP_OFFSET_PX,
  TOOLTIP_PLACEMENT,
  TOOLTIP_VIEWPORT_PADDING_PX,
} from './tooltip.constants';

const ARIA_DESCRIBEDBY = 'aria-describedby';
const ARIA_LABEL = 'aria-label';

/**
 * Rendu de l'infobulle unique de l'application.
 *
 * Une seule bulle `role="tooltip"`, posée dans le `<body>` — hors de tout
 * `overflow` qui la rognerait — et placée par Floating UI : au-dessus de
 * l'élément, retournée dessous si la place manque, décalée pour ne jamais
 * sortir de l'écran.
 *
 * Affichage synchrone : aucun délai. Le placement se calcule en micro-tâche,
 * donc avant que le navigateur ne peigne la bulle.
 *
 * Accessibilité : l'élément décrit reçoit `aria-describedby` vers la bulle,
 * SAUF si le texte est déjà son nom accessible (`aria-label`) — un lecteur
 * d'écran l'annoncerait deux fois.
 */
@Injectable({ providedIn: 'root' })
export class TooltipService implements OnDestroy {
  private readonly document = inject(DOCUMENT);

  private bubble: HTMLElement | null = null;
  private anchor: Element | null = null;
  /** Écarte un placement asynchrone devenu obsolète (bulle déplacée/masquée). */
  private positionRequest = 0;

  /** Élément actuellement décrit par la bulle, `null` si elle est masquée. */
  get currentAnchor(): Element | null {
    return this.anchor;
  }

  show(anchor: Element, text: string): void {
    const bubble = this.ensureBubble();
    if (this.anchor && this.anchor !== anchor) this.unlinkDescription();
    this.anchor = anchor;
    bubble.textContent = text;
    bubble.hidden = false;
    this.linkDescription(anchor, text);
    this.position(anchor, bubble);
  }

  hide(): void {
    this.positionRequest++;
    if (this.bubble) this.bubble.hidden = true;
    this.unlinkDescription();
    this.anchor = null;
  }

  ngOnDestroy(): void {
    this.hide();
    this.bubble?.remove();
    this.bubble = null;
  }

  private ensureBubble(): HTMLElement {
    if (this.bubble) return this.bubble;
    const bubble = this.document.createElement('div');
    bubble.id = TOOLTIP_ELEMENT_ID;
    bubble.className = TOOLTIP_CLASS;
    bubble.setAttribute('role', 'tooltip');
    bubble.hidden = true;
    this.document.body.appendChild(bubble);
    this.bubble = bubble;
    return bubble;
  }

  private position(anchor: Element, bubble: HTMLElement): void {
    const request = ++this.positionRequest;
    computePosition(anchor, bubble, {
      placement: TOOLTIP_PLACEMENT,
      strategy: 'fixed',
      middleware: [
        offset(TOOLTIP_OFFSET_PX),
        flip({ padding: TOOLTIP_VIEWPORT_PADDING_PX }),
        shift({ padding: TOOLTIP_VIEWPORT_PADDING_PX }),
      ],
    }).then(({ x, y }) => {
      if (request !== this.positionRequest) return;
      // Coordonnées calculées à l'exécution : seules propriétés posées en
      // style, le reste de l'apparence vit dans tooltip.scss.
      bubble.style.left = `${x}px`;
      bubble.style.top = `${y}px`;
    });
  }

  private linkDescription(anchor: Element, text: string): void {
    if (anchor.getAttribute(ARIA_LABEL)?.trim() === text) {
      this.removeDescribedBy(anchor);
      return;
    }
    const ids = this.describedByIds(anchor);
    if (!ids.includes(TOOLTIP_ELEMENT_ID)) {
      anchor.setAttribute(
        ARIA_DESCRIBEDBY,
        [...ids, TOOLTIP_ELEMENT_ID].join(' ')
      );
    }
  }

  private unlinkDescription(): void {
    if (this.anchor) this.removeDescribedBy(this.anchor);
  }

  private removeDescribedBy(anchor: Element): void {
    const ids = this.describedByIds(anchor).filter(
      (id) => id !== TOOLTIP_ELEMENT_ID
    );
    if (ids.length) anchor.setAttribute(ARIA_DESCRIBEDBY, ids.join(' '));
    else anchor.removeAttribute(ARIA_DESCRIBEDBY);
  }

  private describedByIds(anchor: Element): string[] {
    return (anchor.getAttribute(ARIA_DESCRIBEDBY) ?? '')
      .split(/\s+/)
      .filter(Boolean);
  }
}
